import React, { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineClose, AiOutlineDown, AiOutlineFilter, AiOutlineSortAscending, AiOutlineSortDescending } from "react-icons/ai";
import Pagination from "./Pagination";
import { formatDateTime } from "../utils/dateUtils";
import { cargarPrefs, cargarLista, guardarPrefs } from "../utils/prefsLocales";
import { AUDIT_EVENTS, PARES_POR_DEFECTO, migrarPares, entradaDe, eventosVisibles } from "../config/auditEvents";

const CLAVE_EVENTOS = "audit.eventos";
const CLAVE_ORDEN = "audit.orden";

/**
 * Ventana de auditoría de un documento (#162).
 *
 * Antes mostraba dos sucesos de los quince que el sistema registra, porque la consulta filtraba por
 * un tipo fijo. Ahora el backend devuelve todo y acá se elige qué ver, con los dos de siempre
 * encendidos por defecto para que la vista no cambie sin que nadie lo pida.
 *
 * Dos decisiones que se leen mejor juntas:
 *
 * - **Se persisten los pares `(tipo, suceso)` y no las claves de negocio**, así el día que un
 *   nombre se parta en dos —"Rechazado" agrupa hoy el rechazo en aprobación y el de firma— quien lo
 *   tenía elegido sigue viendo los dos en vez de quedarse con una clave muerta.
 * - **No se avisa cuántos eventos quedan fuera de la vista** (issue UBA #59). El aviso existía por
 *   una razón buena —en una traza lo que no se ve se interpreta como que no pasó— pero en la
 *   práctica se disparaba siempre por los pasos intermedios de la importación, que se ocultan a
 *   propósito: le decía al usuario que faltaban tres eventos cuando esos tres son ruido. Lo que
 *   protege del caso real —un evento que el backend emite y el catálogo no sabe nombrar— es el test
 *   de cobertura de `auditEvents.test.js`, que obliga a declararlo como visible u oculto.
 */
const AuditoriaModal = ({ eventos = [], error = null, onCerrar }) => {
  // La preferencia guardada se traduce al vocabulario vigente antes de usarla (#187): los pares se
  // persisten como texto, así que sin esto quien tenía "Aprobado" elegido dejaría de verlo en
  // silencio después del renombre — sin error, sin aviso, y sin forma de sospechar por qué.
  const [elegidos, setElegidos] = useState(() => {
    const guardado = cargarLista(CLAVE_EVENTOS, PARES_POR_DEFECTO);
    const migrado = migrarPares(guardado);
    // Se persiste la traducción: si sólo se migrara al leer, quien nunca toque el selector conserva
    // las claves viejas para siempre y el día que se borre CLAVES_MIGRADAS deja de ver sus eventos
    // en silencio.
    if (migrado.join('|') !== guardado.join('|')) guardarPrefs(CLAVE_EVENTOS, migrado);
    return migrado;
  });
  const [descendente, setDescendente] = useState(() => cargarPrefs(CLAVE_ORDEN, { desc: true }).desc !== false);
  const [abierto, setAbierto] = useState(false);
  const [pagina, setPagina] = useState({ page: 0, size: 10, total: 0 });
  const selectorRef = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    const afuera = (e) => { if (selectorRef.current && !selectorRef.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", afuera);
    return () => document.removeEventListener("mousedown", afuera);
  }, [abierto]);

  const visibles = useMemo(() => {
    const filtrados = eventosVisibles(eventos, elegidos);
    // Por instante y no por string: `localeCompare` pone '...:05Z' después de '...:05.500Z'
    // porque el punto colaciona antes que la Z, así que el día que la columna gane fracción de
    // segundo el orden se ensucia sin que nada falle. Y con `id_evento` como desempate, porque
    // la columna tiene resolución de un segundo y los empates son masivos: sin esto, dos
    // aperturas del mismo modal pueden mostrar el mismo par de eventos en orden distinto.
    const instante = (e) => new Date(e.createdAt || 0).getTime();
    return [...filtrados].sort((a, b) => {
      const cmp = (instante(a) - instante(b)) || (Number(a.eventId) - Number(b.eventId));
      return descendente ? -cmp : cmp;
    });
  }, [eventos, elegidos, descendente]);


  const cambiarSeleccion = (pares) => {
    setElegidos(pares);
    guardarPrefs(CLAVE_EVENTOS, pares);
    // Sin esto se puede quedar en la página 3 de una lista que ahora tiene una fila.
    setPagina((p) => ({ ...p, page: 0 }));
  };

  // `some` y no `every`: una entrada puede GANAR un par —le pasó a "Importado" cuando apareció
  // la importación por Telegram—, y con `every` el usuario que tenía guardado el par viejo veía
  // sus filas en la grilla pero el checkbox desmarcado. El `Set` evita duplicar al re-marcar.
  const estaActivo = (evento) => evento.pares.some((p) => elegidos.includes(p.clave));

  const alternar = (evento) => {
    const claves = evento.pares.map((p) => p.clave);
    cambiarSeleccion(estaActivo(evento)
      ? elegidos.filter((c) => !claves.includes(c))
      : [...new Set([...elegidos, ...claves])]);
  };

  const alternarOrden = () => {
    const proximo = !descendente;
    setDescendente(proximo);
    guardarPrefs(CLAVE_ORDEN, { desc: proximo });
  };

  const activos = AUDIT_EVENTS.filter(estaActivo).length;
  const desde = pagina.page * pagina.size;
  const enPantalla = visibles.slice(desde, desde + pagina.size);

  return (
    <div style={styles.overlay}>
      <div style={styles.contenido} role="dialog" aria-modal="true" aria-label="Eventos de auditoría">
        <button onClick={onCerrar} style={styles.cerrar} title="Cerrar" aria-label="Cerrar">
          <AiOutlineClose />
        </button>

        <h3 style={styles.titulo}>Eventos de auditoría</h3>

        <div style={styles.controles}>
          <div style={{ position: "relative" }} ref={selectorRef}>
            <button type="button" onClick={() => setAbierto((v) => !v)} style={styles.boton} aria-expanded={abierto} aria-haspopup="true">
              <AiOutlineFilter /> Mostrando {activos} de {AUDIT_EVENTS.length} tipos <AiOutlineDown style={{ fontSize: 11 }} />
            </button>
            {abierto && (
              <div style={styles.desplegable} role="group" aria-label="Tipos de evento">
                {AUDIT_EVENTS.map((e) => (
                  <label key={e.key} style={styles.opcion}>
                    <input
                      type="checkbox"
                      checked={estaActivo(e)}
                      onChange={() => alternar(e)}
                    />
                    <span>{e.label}</span>
                  </label>
                ))}
                <button type="button" style={styles.restablecer} onClick={() => cambiarSeleccion(PARES_POR_DEFECTO)}>
                  Restablecer
                </button>
              </div>
            )}
          </div>

          <button type="button" onClick={alternarOrden} style={styles.boton}
                  aria-label={descendente ? "Orden: más recientes primero. Cambiar a más antiguos primero" : "Orden: más antiguos primero. Cambiar a más recientes primero"}>
            {descendente ? <AiOutlineSortDescending /> : <AiOutlineSortAscending />}
            {descendente ? "Más recientes primero" : "Más antiguos primero"}
          </button>
        </div>

        {error ? (
          <p style={styles.mensaje}>{error}</p>
        ) : (
          <>
            <table style={styles.tabla}>
              <thead>
                <tr>
                  <th style={styles.th}>Evento</th>
                  <th style={styles.th}>Usuario</th>
                  <th style={styles.th}>Fecha y hora</th>
                </tr>
              </thead>
              <tbody>
                {enPantalla.length > 0 ? (
                  enPantalla.map((ev) => (
                    <tr key={ev.eventId}>
                      <td style={styles.td}>{entradaDe(ev)?.label}</td>
                      <td style={styles.td}>{ev.user || "-"}</td>
                      <td style={styles.td}>{formatDateTime(ev.createdAt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={styles.vacio}>
                      {/* Los dos vacíos dicen cosas distintas a propósito: si el usuario desmarca
                          todo y lee el mismo texto que un documento sin historial, concluye que el
                          sistema perdió los datos. */}
                      {eventos.length === 0
                        ? "Este documento no tiene eventos registrados."
                        : "Ningún evento coincide con los tipos elegidos."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {visibles.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Pagination
                  value={{ ...pagina, total: visibles.length }}
                  onChange={(next) => setPagina(next)}
                />
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  contenido: {
    backgroundColor: "#fff", borderRadius: 8, padding: 24, minWidth: 640,
    maxWidth: "90vw", maxHeight: "85vh", overflowY: "auto", position: "relative",
  },
  cerrar: {
    position: "absolute", top: 12, right: 12, border: "none",
    background: "transparent", cursor: "pointer", fontSize: 18,
  },
  titulo: { marginBottom: 12 },
  controles: { display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" },
  boton: {
    display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
    backgroundColor: "#fff", color: "#00468B", border: "1px solid #00468B",
    borderRadius: 4, cursor: "pointer", fontSize: 14,
  },
  desplegable: {
    position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20, minWidth: 220,
    backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 4,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)", padding: 8,
    display: "flex", flexDirection: "column", gap: 6,
  },
  opcion: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", padding: 4 },
  restablecer: {
    marginTop: 4, padding: "4px 8px", fontSize: 13, cursor: "pointer",
    background: "transparent", border: "1px solid #ddd", borderRadius: 4, color: "#374151",
  },
  tabla: { width: "100%", borderCollapse: "collapse", border: "1px solid #ccc" },
  th: { backgroundColor: "#00468B", color: "#fff", padding: 12, textAlign: "left", border: "1px solid #ccc" },
  td: { padding: 12, border: "1px solid #ccc", fontSize: 14, color: "#333" },
  vacio: { textAlign: "center", fontStyle: "italic", padding: 20, color: "#666" },
  mensaje: { textAlign: "center", padding: 20, color: "#B91C1C" },
};

export default AuditoriaModal;
